import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Check, Zap, Users, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';

const Pricing = () => {
  const { isAuthenticated } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setIsVisible(true);
    if (isAuthenticated()) {
      fetchSubscriptionData();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      const subscriptionData = await api.get('/subscription.php');
      console.log('Subscription data:', subscriptionData);

      if (subscriptionData && subscriptionData.success === false) {
        console.warn('Subscription API error:', subscriptionData.error);
        setCurrentSubscription({
          is_active: false,
          plan: 'free',
          plan_limits: { features: [] }
        });
      } else {
        setCurrentSubscription(subscriptionData);
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      setCurrentSubscription({
        is_active: false,
        plan: 'free',
        plan_limits: { features: [] }
      });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPlan = () => {
    if (!currentSubscription) return 'free';
    return currentSubscription.subscription?.plan || currentSubscription.plan_limits?.plan || 'free';
  };

  const canUpgrade = () => {
    const currentPlan = getCurrentPlan();
    return currentPlan !== 'enterprise';
  };

  const handleUpgrade = async (plan) => {
    try {
      setIsProcessing(true);

      // Send plan name with interval suffix
      const response = await api.post('/checkout-session.php', {
        plan: `${plan.id}_${billingPeriod}`,  
        is_trial: false,
        interval: billingPeriod
      });

      console.log('Checkout response:', response);

      // Check if response has success property
      if (response.success === false) {
        throw new Error(response.error || 'Checkout failed');
      }

      // Get the checkout URL from response
      const checkoutUrl = response.url;

      if (!checkoutUrl) {
        throw new Error('No checkout URL received');
      }

      // Redirect directly to the Stripe checkout URL
      window.location.href = checkoutUrl;

    } catch (error) {
      console.error('Upgrade error:', error);
      toast.error(t('pricing.upgradeError', 'Upgrade failed: ') + error.message);
      setIsProcessing(false);
    }
  };

  const plans = [
    {
      name: t('pricing.plans.free.name', 'Free'),
      description: t('pricing.plans.free.description', 'Perfect for getting started'),
      price: {
        monthly: 0,
        yearly: 0
      },
      popular: false,
      icon: Zap,
      gradient: 'from-gray-500 to-gray-600',
      features: [
        t('pricing.plans.free.features.0', 'Up to 3 staff accounts'),
        t('pricing.plans.free.features.1', 'Basic QR menu'),
        t('pricing.plans.free.features.2', '50 orders/month'),
        t('pricing.plans.free.features.3', 'Email support'),
        t('pricing.plans.free.features.4', '5 tables')
      ],
      cta: t('pricing.plans.free.cta', 'Get Started Free'),
      highlighted: false,
      id: 'free',
      limits: {
        max_staff: 3,
        max_orders: 50,
        max_tables: 5
      }
    },
    { 
      name: t('pricing.plans.pro.name', 'Professional'),
      description: t('pricing.plans.pro.description', 'For growing restaurants'),
      price: {
        monthly: 99,
        yearly: 1089
      },
      popular: true,
      icon: Users,
      gradient: 'from-purple-500 to-violet-500',
      features: [
        t('pricing.plans.pro.features.0', 'Up to 15 staff members'),
        t('pricing.plans.pro.features.1', 'Up to 50 tables'),
        t('pricing.plans.pro.features.2', 'Advanced menu management'),
        t('pricing.plans.pro.features.3', 'Real-time order tracking'),
        t('pricing.plans.pro.features.4', 'Analytics dashboard'),
        t('pricing.plans.pro.features.5', 'Priority email support'),
        t('pricing.plans.pro.features.6', 'Custom branding'),
        t('pricing.plans.pro.features.7', 'Unlimited orders')
      ],
      cta: t('pricing.plans.pro.cta', 'Start Professional'),
      highlighted: true,
      id: 'pro',
      // Stripe price IDs should be configured for both monthly and yearly
      // Note: You need to get actual price IDs from your Stripe dashboard
      monthly_price_id: 'price_1TOOq57qJJQapbT0ZozM6b2Y',
      yearly_price_id: 'price_1TOOqn7qJJQapbT0jnMQjVba', // Yearly Pro - Update with your actual yearly price ID
      limits: {
        max_staff: -1,
        max_tables: -1,
        max_orders: -1
      }
    }
  ];

  // Get current plan details
  const currentPlan = getCurrentPlan();
  const currentPlanDetails = plans.find(plan => plan.id === currentPlan);
  const isActive = currentSubscription?.is_active || false;

  // Get FAQs from translations using individual keys
  const faqs = [
    {
      question: t('pricing.faq.questions.0.question', 'Can I change plans later?'),
      answer: t('pricing.faq.questions.0.answer', 'Yes, you can upgrade or downgrade your plan at any time. Upgrades take effect immediately, while downgrades apply at the end of your billing cycle.')
    },
    {
      question: t('pricing.faq.questions.1.question', 'Is there a free trial?'),
      answer: t('pricing.faq.questions.1.answer', 'We offer a 30-day free trial for Professional and Enterprise plans. No credit card required to start.')
    },
    {
      question: t('pricing.faq.questions.2.question', 'What payment methods do you accept?'),
      answer: t('pricing.faq.questions.2.answer', 'We accept all major credit cards through Stripe. We also support ACH bank transfers for Enterprise plans.')
    },
    {
      question: t('pricing.faq.questions.3.question', 'Can I cancel anytime?'),
      answer: t('pricing.faq.questions.3.answer', 'Yes, you can cancel your subscription at any time. You will continue to have access until the end of your billing period.')
    }
  ];

  // Plan colors for styling
  const planColors = {
    free: {
      bg: 'bg-gray-50',
      border: 'border-gray-300',
      ring: 'ring-gray-200',
      text: 'text-gray-700',
      badge: 'bg-gray-600'
    },
    pro: {
      bg: 'bg-blue-50',
      border: 'border-blue-500',
      ring: 'ring-blue-200',
      text: 'text-blue-700',
      badge: 'bg-blue-600'
    },
    enterprise: {
      bg: 'bg-purple-50',
      border: 'border-purple-500',
      ring: 'ring-purple-200',
      text: 'text-purple-700',
      badge: 'bg-purple-600'
    }
  };

  if (loading && isAuthenticated()) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[linear-gradient(to_right,#f0f0f0_1px,transparent_1px),linear-gradient(to_bottom,#f0f0f0_1px,transparent_1px)] bg-[size:6rem_4rem]">
        <div className="absolute bottom-0 left-0 right-0 top-0 bg-[radial-gradient(circle_800px_at_50%_100px,#eef2ff,transparent)]"></div>
      </div>

      {/* Header Section */}
      <section className="relative py-20 lg:py-28">
        <div className="container relative">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="h-4 w-4" />
              {t('pricing.badge', 'No credit card required to start')}
            </div>

            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              {t('pricing.title.part1', 'Simple, transparent ')}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {t('pricing.title.part2', 'pricing')}
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              {t('pricing.subtitle', 'Choose the perfect plan for your restaurant. All plans include our core features.')}
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center bg-gray-100 rounded-2xl p-1 mb-12">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${billingPeriod === 'monthly'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {t('pricing.monthly', 'Monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('yearly')}
                className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${billingPeriod === 'yearly'
                  ? 'bg-white text-gray-900 shadow-lg'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {t('pricing.yearly', 'Yearly')} <span className="text-green-600 ml-1">
                  {t('pricing.save20', 'Save 10%')}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Plans */}
      <section className="py-10 lg:py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan, index) => {
              const Icon = plan.icon;
              const isCurrentPlan = plan.id === currentPlan && isAuthenticated();
              const currentPlanStyle = planColors[plan.id] || planColors.free;
              const isUpgrade = canUpgrade() && plan.price[billingPeriod] > 0 && !isCurrentPlan;

              return (
                <div
                  key={plan.name}
                  className={`relative transition-all duration-500 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                    }`}
                  style={{ animationDelay: `${index * 200}ms` }}
                >

                  <div className={`relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 h-full flex flex-col border-2 ${isCurrentPlan
                    ? `${currentPlanStyle.border} ring-4 ${currentPlanStyle.ring}`
                    : plan.highlighted
                      ? 'border-purple-200'
                      : 'border-gray-100'
                    }`}>
                    <div className="p-8 flex-1">
                      {/* Plan Header */}
                      <div className="text-center mb-8">
                        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${plan.gradient} text-white mb-4`}>
                          <Icon className="h-8 w-8" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <p className="text-gray-600">{plan.description}</p>
                      </div>

                      {/* Price */}
                      <div className="text-center mb-8">
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-4xl md:text-5xl font-bold text-gray-900">
                            CHF {plan.price[billingPeriod]}
                          </span>
                          <span className="text-gray-600">
                            {plan.price[billingPeriod] > 0
                              ? `/${t('pricing.perMonth', 'month')}`
                              : t('pricing.freeForever', 'free forever')
                            }
                          </span>
                        </div>
                        {billingPeriod === 'yearly' && plan.price[billingPeriod] > 0 && (
                          <p className="text-green-600 font-medium mt-2">
                            {t('pricing.billedAnnually', 'Billed annually')}
                          </p>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-4 mb-8">
                        {plan.features.map((feature, featureIndex) => (
                          <li key={featureIndex} className="flex items-center gap-3">
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <Check className="h-4 w-4 text-green-600" />
                            </div>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* CTA Button */}
                    <div className="p-8 pt-0">
                      {isAuthenticated() ? (
                        isCurrentPlan ? (
                          <div className={`w-full ${currentPlanStyle.bg} ${currentPlanStyle.text} py-4 px-6 rounded-xl text-center font-semibold border ${currentPlanStyle.border}`}>
                            {t('pricing.currentPlan', 'Current Plan')}
                          </div>
                        ) : plan.id === 'free' ? (
                          <Link
                            to="/dashboard"
                            className="block w-full text-center py-4 px-6 rounded-xl font-semibold transition-all duration-300 bg-gray-100 text-gray-900 hover:bg-gray-200"
                          >
                            {t('pricing.continueFree', 'Continue Free')}
                          </Link>
                        ) : isUpgrade ? (
                          <button
                            onClick={() => handleUpgrade(plan)}
                            disabled={isProcessing}
                            className={`w-full text-center py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${plan.highlighted
                              ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:shadow-xl hover:scale-105'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                              } disabled:opacity-50`}
                          >
                            {isProcessing
                              ? t('common.loading', 'Processing...')
                              : billingPeriod === 'yearly'
                                ? t('pricing.upgradeYearly', 'Upgrade Yearly')
                                : t('pricing.upgradeNow', 'Upgrade Now')
                            }
                          </button>
                        ) : (
                          <Link
                            to="/register"
                            className="block w-full text-center py-4 px-6 rounded-xl font-semibold transition-all duration-300 border-2 border-gray-200 text-gray-900 hover:border-blue-600 hover:text-blue-600"
                          >
                            {t('pricing.contactSales', 'Contact Sales')}
                          </Link>
                        )
                      ) : (
                        <Link
                          to="/register"
                          className={`block w-full text-center py-4 px-6 rounded-xl font-semibold transition-all duration-300 ${plan.highlighted
                            ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:shadow-xl hover:scale-105'
                            : plan.id === 'free'
                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                              : 'border-2 border-gray-200 text-gray-900 hover:border-blue-600 hover:text-blue-600'
                            }`}
                        >
                          {plan.cta}
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('pricing.faq.title', 'Frequently asked questions')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('pricing.faq.subtitle', 'Everything you need to know about our pricing and plans')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <div className="bg-white rounded-3xl p-8 shadow-lg max-w-2xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                {t('pricing.bottomCta.title', 'Still have questions?')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('pricing.bottomCta.subtitle', 'Our team is here to help you choose the right plan for your restaurant.')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {t('pricing.bottomCta.contactSales', 'Contact Sales')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;